import { useMemo, useRef, useState } from "react";
import { findSpans, FormatSpan } from "../RichText";

interface UseBodyEditorParams {
  value: string;
  onChange: (next: string) => void;
}

export function useBodyEditor({ value, onChange }: UseBodyEditorParams) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const capturedSelection = useRef<{ start: number; end: number } | null>(null);

  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  const spans = useMemo(() => findSpans(value), [value]);
  const activeFormats = useMemo(
    () => computeActiveFormats(spans, selection.start, selection.end),
    [spans, selection]
  );

  const refreshSelection = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    setSelection({ start: ta.selectionStart ?? 0, end: ta.selectionEnd ?? 0 });
  };

  const wrapSelection = (marker: string) => {
    const ta = textareaRef.current;
    if (!ta) return;

    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const selected = value.slice(start, end);

    if (selected.length > 0) {
      const next =
        value.slice(0, start) + marker + selected + marker + value.slice(end);

      onChange(next);

      requestAnimationFrame(() => {
        ta.focus();
        const innerStart = start + marker.length;
        const innerEnd = innerStart + selected.length;
        ta.setSelectionRange(innerStart, innerEnd);
        setSelection({ start: innerStart, end: innerEnd });
      });
    } else {
      const next = value.slice(0, start) + marker + marker + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        ta.focus();
        const cursor = start + marker.length;
        ta.setSelectionRange(cursor, cursor);
        setSelection({ start: cursor, end: cursor });
      });
    }
  };

  const removeWrapper = (
    span: FormatSpan,
    selStart: number,
    selEnd: number
  ) => {
    const ta = textareaRef.current;
    if (!ta) return;

    const before = value.slice(0, span.outerStart);
    const inner = value.slice(span.innerStart, span.innerEnd);
    const after = value.slice(span.outerEnd);
    const next = before + inner + after;

    onChange(next);

    const markerLen = span.innerStart - span.outerStart; // 2 for ** and __
    const innerLen = span.innerEnd - span.innerStart;
    const adjustPos = (pos: number) => {
      if (pos <= span.outerStart) return pos;
      if (pos >= span.outerEnd) return pos - 2 * markerLen;
      if (pos <= span.innerStart) return span.outerStart;
      if (pos >= span.innerEnd) return span.outerStart + innerLen;
      return pos - markerLen;
    };

    const newStart = adjustPos(selStart);
    const newEnd = adjustPos(selEnd);

    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newStart, newEnd);
      setSelection({ start: newStart, end: newEnd });
    });
  };

  const toggle = (type: "bold" | "underline", marker: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;

    const enclosing = findEnclosingSpan(spans, type, start, end);
    if (enclosing) {
      removeWrapper(enclosing, start, end);
    } else {
      wrapSelection(marker);
    }
  };

  const bold = () => toggle("bold", "**");
  const underline = () => toggle("underline", "__");

  const openLink = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;

    const existing = findEnclosingSpan(spans, "link", start, end);

    if (existing) {
      capturedSelection.current = {
        start: existing.outerStart,
        end: existing.outerEnd,
      };
      setLinkText(existing.linkText ?? "");
      setLinkUrl(existing.linkUrl ?? "");
    } else {
      const selected = value.slice(start, end);
      capturedSelection.current = { start, end };
      setLinkText(selected);
      setLinkUrl("");
    }
    setLinkError(null);
    setLinkOpen(true);
  };

  const cancelLink = () => {
    setLinkOpen(false);
    setLinkError(null);
    capturedSelection.current = null;
  };

  const insertLink = () => {
    const text = linkText.trim();
    const url = linkUrl.trim();

    if (!text) {
      setLinkError("Link text is required.");
      return;
    }
    // The link-text capture group in our parser is `[^\]]+` — anything that
    // isn't `]`. A `[` or `]` in the text would either truncate the link or
    // leak markdown syntax into the rendered output. Reject up front rather
    // than try to escape it (the parser doesn't know about escapes).
    if (/[\[\]]/.test(text)) {
      setLinkError("Link text can't contain '[' or ']' characters.");
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      setLinkError("URL must start with http:// or https://");
      return;
    }

    const ta = textareaRef.current;
    const sel = capturedSelection.current;
    if (!ta || !sel) {
      cancelLink();
      return;
    }

    const inserted = `[${text}](${url})`;
    const next = value.slice(0, sel.start) + inserted + value.slice(sel.end);

    onChange(next);
    cancelLink();

    requestAnimationFrame(() => {
      ta.focus();
      const cursor = sel.start + inserted.length;
      ta.setSelectionRange(cursor, cursor);
      setSelection({ start: cursor, end: cursor });
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;

    const k = e.key.toLowerCase();
    if (k === "b") {
      e.preventDefault();
      bold();
    } else if (k === "u") {
      e.preventDefault();
      underline();
    } else if (k === "k") {
      e.preventDefault();
      openLink();
    }
  };

  return {
    textareaRef,
    handleKeyDown,
    refreshSelection,
    bold,
    underline,
    openLink,
    activeFormats,
    linkDraft: {
      open: linkOpen,
      text: linkText,
      url: linkUrl,
      error: linkError,
      setText: setLinkText,
      setUrl: setLinkUrl,
      insert: insertLink,
      cancel: cancelLink,
    },
  };
}

function isSelectionInside(
  span: FormatSpan,
  selStart: number,
  selEnd: number
): boolean {
  if (selStart === selEnd) {
    return selStart > span.outerStart && selStart < span.outerEnd;
  }
  return selStart >= span.outerStart && selEnd <= span.outerEnd;
}

function computeActiveFormats(
  spans: FormatSpan[],
  selStart: number,
  selEnd: number
) {
  let bold = false;
  let underline = false;
  let link: FormatSpan | null = null;

  for (const span of spans) {
    if (!isSelectionInside(span, selStart, selEnd)) continue;
    if (span.type === "bold") bold = true;
    else if (span.type === "underline") underline = true;
    else if (span.type === "link") {
      if (
        !link ||
        span.outerEnd - span.outerStart < link.outerEnd - link.outerStart
      ) {
        link = span;
      }
    }
  }

  return { bold, underline, link };
}

function findEnclosingSpan(
  spans: FormatSpan[],
  type: FormatSpan["type"],
  selStart: number,
  selEnd: number
): FormatSpan | null {
  const matches = spans
    .filter((s) => s.type === type && isSelectionInside(s, selStart, selEnd))
    .sort((a, b) => a.outerEnd - a.outerStart - (b.outerEnd - b.outerStart));
  return matches[0] ?? null;
}
